/**
 * Admin Submission Visual Assets Manager
 * Handles upload, TMDB selection, and deletion of platform-specific visual assets
 */

const VisualAssetsManager = {
  initialized: false,
  submissionId: null,
  currentAssetType: null,
  currentRow: null,
  activeOperations: 0,
  savedScrollPosition: 0,

  init () {
    // Prevent duplicate initialization
    if (this.initialized) {
      console.log('Visual Assets Manager already initialized');
      return;
    }

    // Only run on admin submission view pages
    if ($('#visual-assets-section').length === 0) {
      return;
    }

    this.submissionId = $('#visual-assets-section').find('tr[data-submission-id]').first().data('submission-id');

    if (!this.submissionId) {
      console.error('Submission ID not found');
      return;
    }

    this.bindEvents();
    this.loadImageDimensions();
    this.initFileUploads();
    this.setupNavigationProtection();
    this.initialized = true;
    console.log('Visual Assets Manager initialized for submission:', this.submissionId);
  },

  setupNavigationProtection () {
    // Prevent navigation when operations are active
    window.addEventListener('beforeunload', (e) => {
      if (this.activeOperations > 0) {
        const message = 'Asset operations are still in progress. Are you sure you want to leave?';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    });
  },

  loadImageDimensions () {
    // Find all asset preview images and load their actual dimensions
    $('#visual-assets-section .asset-preview-link img').each(function() {
      const $img = $(this);
      const $row = $img.closest('tr');
      const $parent = $img.closest('td');
      const $dimensionsText = $parent.find('small.text-muted');

      if ($dimensionsText.length === 0) {
        return; // No dimensions text element found
      }

      // Get expected dimensions from the status & dimensions column (column 2 = index 1)
      const $statusCell = $row.find('td').eq(1);
      const cellText = $statusCell.text();
      const needMatch = cellText.match(/Need:\s*(\d+)\s*×\s*(\d+)/);
      const expectedMatch = cellText.match(/Expected:\s*(\d+)\s*×\s*(\d+)/);

      let expectedWidth, expectedHeight;
      if (needMatch) {
        expectedWidth = parseInt(needMatch[1]);
        expectedHeight = parseInt(needMatch[2]);
      } else if (expectedMatch) {
        expectedWidth = parseInt(expectedMatch[1]);
        expectedHeight = parseInt(expectedMatch[2]);
      } else {
        return; // Can't parse expected dimensions
      }

      // Create a new image to load and get natural dimensions
      const img = new Image();
      img.onload = function() {
        const actualWidth = this.naturalWidth;
        const actualHeight = this.naturalHeight;
        const actualDims = `${actualWidth} × ${actualHeight}`;
        const currentText = $dimensionsText.text();

        // Check if dimensions match
        const dimensionsMatch = (actualWidth === expectedWidth && actualHeight === expectedHeight);

        let displayText;
        if (dimensionsMatch) {
          // Dimensions match - show in normal color
          displayText = currentText ? `${actualDims} • ${currentText}` : actualDims;
          $dimensionsText.html(displayText);
          $dimensionsText.removeClass('text-warning');
        } else {
          // Dimensions don't match - show warning
          displayText = currentText
            ? `<i class="fas fa-exclamation-triangle"></i> ${actualDims} • ${currentText}`
            : `<i class="fas fa-exclamation-triangle"></i> ${actualDims}`;
          $dimensionsText.html(displayText);
          $dimensionsText.addClass('text-warning');
          $dimensionsText.attr('title', `Expected: ${expectedWidth}×${expectedHeight}`);
        }
      };
      img.src = $img.attr('src');
    });
  },

  cleanupFileUploads (container) {
    // Clean up any existing FileUpload instances before DOM replacement
    // If container is provided, only clean up within that container
    const selector = '.file-upload-area';
    const dropzones = container
      ? container.querySelectorAll(selector)
      : document.querySelectorAll(selector);

    console.log('[VisualAssetsManager] Cleaning up', dropzones.length, 'file upload dropzones', container ? 'in container' : 'globally');

    dropzones.forEach(element => {
      if (element.fileUpload) {
        // Call unbindEvents to remove all event listeners
        if (typeof element.fileUpload.unbindEvents === 'function') {
          element.fileUpload.unbindEvents();
          console.log('[VisualAssetsManager] Unbound events for:', element);
        }
        // Remove the reference
        delete element.fileUpload;
      }
    });
  },

  initFileUploads () {
    // FileUpload now auto-initializes itself, but we call initAll() after AJAX updates
    // to ensure any newly added dropzones are initialized
    if (typeof FileUpload !== 'undefined' && typeof FileUpload.initAll === 'function') {
      console.log('[VisualAssetsManager] Calling FileUpload.initAll() to initialize dropzones');
      FileUpload.initAll();
    } else {
      console.warn('[VisualAssetsManager] FileUpload.initAll() not available');
    }
  },

  // Legacy method - kept for backwards compatibility but now just calls initAll
  _oldInitFileUploads () {
    // Initialize FileUpload components for dropzones
    // This is needed because the dropzones are loaded dynamically via AJAX
    const dropzones = document.querySelectorAll('.file-upload-area');
    console.log('[VisualAssetsManager] Found', dropzones.length, 'file upload dropzones');

    dropzones.forEach((element, index) => {
      console.log(`[VisualAssetsManager] Dropzone ${index}:`, element);
      console.log(`[VisualAssetsManager] Has fileUpload?`, !!element.fileUpload);
      console.log(`[VisualAssetsManager] FileUpload class available?`, typeof FileUpload !== 'undefined');

      // Only initialize if not already initialized
      if (!element.fileUpload) {
        // Check if FileUpload class is available
        if (typeof FileUpload !== 'undefined') {
          try {
            const fileUpload = new FileUpload(element);
            element.fileUpload = fileUpload;
            console.log('[VisualAssetsManager] ✓ FileUpload initialized for dropzone', index);
          } catch (error) {
            console.error('[VisualAssetsManager] ✗ Error initializing FileUpload:', error);
          }
        } else {
          console.error('[VisualAssetsManager] FileUpload class not available');
        }
      } else {
        console.log('[VisualAssetsManager] FileUpload already initialized for dropzone', index);
      }
    });
  },

  bindEvents () {
    // Unbind all events first to prevent double-binding
    $(document).off('click', '#download-bundle-btn');
    $(document).off('click', '.asset-upload-btn');
    $(document).off('change', '#asset-upload-input');
    $(document).off('click', '.asset-tmdb-btn');
    $(document).off('click', '.asset-delete-btn');
    $(document).off('click', '.asset-upscale-btn');
    $(document).off('click', '.asset-resize-btn');
    $(document).off('click', '.asset-crop-btn');
    $(document).off('click', '.asset-manual-crop-btn');
    $(document).off('click', '.asset-copy-btn');
    $(document).off('click', '.asset-magic-btn');
    $(document).off('click', '.video-upload-btn');
    $(document).off('click', '.video-delete-btn');
    $(document).off('click', '.agreement-upload-btn');
    $(document).off('click', '.agreement-delete-btn');
    $(document).off('click', '.subtitle-upload-btn');
    $(document).off('click', '.subtitle-delete-btn');
    $(document).off('click', '.document-upload-btn');
    $(document).off('click', '.document-delete-btn');

    // Download bundle button click
    $(document).on('click', '#download-bundle-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const submissionId = $btn.data('submission-id');

      if (!submissionId) {
        console.error('Submission ID not found');
        return;
      }

      // Disable button and show loading state
      $btn.prop('disabled', true);
      const originalHtml = $btn.html();
      $btn.html('<i class="fas fa-spinner fa-spin"></i> Preparing Download...');

      // Trigger download
      const downloadUrl = `/api/admin/submissions/${submissionId}/assets/download-bundle`;
      window.location.href = downloadUrl;

      // Re-enable button after a delay (download should have started)
      setTimeout(() => {
        $btn.prop('disabled', false);
        $btn.html(originalHtml);
      }, 3000);
    });

    // Upload button click
    $(document).on('click', '.asset-upload-btn', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const $btn = $(e.currentTarget);

      // Get asset type from data attribute (with fallback for reliability)
      this.currentAssetType = $btn.data('type') || $btn.attr('data-type');
      this.currentRow = $btn.closest('tr');

      // Trigger file input
      const $fileInput = $('#asset-upload-input');
      if ($fileInput[0]) {
        $fileInput[0].click();
      }
    });

    // File input change - remove any existing handlers first to prevent duplicate triggers
    $('#asset-upload-input').off('change').on('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadAsset(file);
      }
      // Reset input
      e.target.value = '';
    });

    // TMDB selection button click
    $(document).on('click', '.asset-tmdb-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      this.currentAssetType = $btn.data('type');
      this.currentRow = $btn.closest('tr');
      this.showTMDBSelector();
    });

    // Delete button click
    $(document).on('click', '.asset-delete-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      const assetType = $btn.data('type');
      this.currentRow = $btn.closest('tr');
      this.deleteAsset(assetId, assetType);
    });

    // Upscale button click
    $(document).on('click', '.asset-upscale-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      this.currentRow = $btn.closest('tr');
      this.showUpscaleModal(assetId);
    });

    // Resize button click
    $(document).on('click', '.asset-resize-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      const expectedWidth = parseInt($btn.data('expected-width'));
      const expectedHeight = parseInt($btn.data('expected-height'));
      this.currentRow = $btn.closest('tr');
      this.showResizeModal(assetId, expectedWidth, expectedHeight);
    });

    // Crop button click (for wrong aspect ratio)
    $(document).on('click', '.asset-crop-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      const assetType = $btn.data('type');
      const expectedWidth = parseInt($btn.data('expected-width'));
      const expectedHeight = parseInt($btn.data('expected-height'));
      this.currentRow = $btn.closest('tr');
      this.currentAssetType = assetType;

      if (!expectedWidth || !expectedHeight) {
        this.showError('Could not determine expected dimensions');
        return;
      }

      // Get the asset image URL to crop
      const $img = this.currentRow.find('.asset-preview-link img');
      if ($img.length > 0) {
        const imageUrl = $img.attr('src');

        // Use download proxy to avoid CORS issues
        const proxyUrl = `/download-proxy?file=${encodeURIComponent(imageUrl)}`;

        // Load the image and show cropper
        fetch(proxyUrl)
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.showCropperModalForExistingAsset(e.target.result, expectedWidth, expectedHeight, assetId);
            };
            reader.readAsDataURL(blob);
          })
          .catch(err => {
            console.error('Crop image load error:', err);
            this.showError('Failed to load image for cropping');
          });
      }
    });

    // Manual Crop button click
    $(document).on('click', '.asset-manual-crop-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      const assetType = $btn.data('type');
      const expectedWidth = parseInt($btn.data('expected-width'));
      const expectedHeight = parseInt($btn.data('expected-height'));
      this.currentRow = $btn.closest('tr');
      this.currentAssetType = assetType;

      if (!expectedWidth || !expectedHeight) {
        this.showError('Could not determine expected dimensions');
        return;
      }

      // Get the asset image URL to crop
      const $img = this.currentRow.find('.asset-preview-link img');
      if ($img.length > 0) {
        const imageUrl = $img.attr('src');

        // Use download proxy to avoid CORS issues
        const proxyUrl = `/download-proxy?file=${encodeURIComponent(imageUrl)}`;

        // Load the image and show cropper
        fetch(proxyUrl)
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.showCropperModalForExistingAsset(e.target.result, expectedWidth, expectedHeight, assetId);
            };
            reader.readAsDataURL(blob);
          })
          .catch(err => {
            console.error('Manual crop image load error:', err);
            this.showError('Failed to load image for cropping');
          });
      }
    });

    // Copy button click
    $(document).on('click', '.asset-copy-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const targetType = $btn.data('type');
      this.currentRow = $btn.closest('tr');
      this.currentAssetType = targetType;
      this.showCopyAssetModal(targetType);
    });

    // Magic button click (intelligent processing)
    $(document).on('click', '.asset-magic-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const assetId = $btn.data('asset-id');
      const assetType = $btn.data('type');
      const expectedWidth = parseInt($btn.data('expected-width'));
      const expectedHeight = parseInt($btn.data('expected-height'));
      this.currentRow = $btn.closest('tr');
      this.currentAssetType = assetType;
      this.performMagicProcessing(assetId, expectedWidth, expectedHeight);
    });

    // Video upload button click
    $(document).on('click', '.video-upload-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      this.currentVideoType = $btn.data('type'); // 'feature' or 'trailer'
      $('#video-upload-input').click();
    });

    // Video file input change
    $('#video-upload-input').off('change').on('change', (e) => {
      const file = e.target.files[0];
      if (file && this.currentVideoType) {
        this.uploadVideo(file, this.currentVideoType);
      }
      e.target.value = '';
    });

    // Video delete button click
    $(document).on('click', '.video-delete-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const uploadId = $btn.data('upload-id');
      const type = $btn.data('type');
      this.deleteVideo(uploadId, type);
    });

    // Document upload button click
    $(document).on('click', '.document-upload-btn', (e) => {
      e.preventDefault();
      $('#document-upload-input').click();
    });

    // Document file input change
    $('#document-upload-input').off('change').on('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadDocument(file);
      }
      e.target.value = '';
    });

    // Document delete button click
    $(document).on('click', '.document-delete-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const uploadId = $btn.data('upload-id');
      this.deleteDocument(uploadId);
    });

    // Agreement upload button click
    $(document).on('click', '.agreement-upload-btn', (e) => {
      e.preventDefault();
      $('#agreement-upload-input').click();
    });

    // Agreement file input change
    $('#agreement-upload-input').off('change').on('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadAgreement(file);
      }
      e.target.value = '';
    });

    // Agreement delete button click
    $(document).on('click', '.agreement-delete-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const uploadId = $btn.data('upload-id');
      this.deleteAgreement(uploadId);
    });

    // Subtitle upload button click
    $(document).on('click', '.subtitle-upload-btn', (e) => {
      e.preventDefault();
      $('#subtitle-upload-input').click();
    });

    // Subtitle file input change
    $('#subtitle-upload-input').off('change').on('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadSubtitle(file);
      }
      e.target.value = '';
    });

    // Subtitle delete button click
    $(document).on('click', '.subtitle-delete-btn', (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      const uploadId = $btn.data('upload-id');
      const language = $btn.data('language');
      this.deleteSubtitle(uploadId, language);
    });

    // FileUpload component event handlers for documents and subtitles
    // Listen for batch upload complete event and refresh page
    $(document).off('batchuploaded', '.file-upload-area');
    $(document).on('batchuploaded', '.file-upload-area', (e) => {
      console.log('Batch upload completed, refreshing page...');
      // Short delay to ensure uploads are fully processed
      setTimeout(() => {
        window.location.reload();
      }, 500);
    });
  },

  uploadAsset (file) {
    if (!this.currentAssetType) {
      this.showError('Asset type not selected');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.showError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      this.showError('File size must be less than 10MB');
      return;
    }

    // Get expected dimensions from row data attributes (set by PHP)
    const expectedWidth = parseInt(this.currentRow.data('expected-width'));
    const expectedHeight = parseInt(this.currentRow.data('expected-height'));

    if (!expectedWidth || !expectedHeight) {
      this.showError('Could not determine expected dimensions');
      return;
    }

    // Load image to check dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const actualWidth = img.width;
        const actualHeight = img.height;

        // A strict size (the Poster, 2000 x 3000) is never enlarged.
        if (this.isStrictRow() && (actualWidth < expectedWidth || actualHeight < expectedHeight)) {
          this.showError(`This image is ${actualWidth} × ${actualHeight}. It must be at least ${expectedWidth} × ${expectedHeight}.`);
          return;
        }

        // Check if dimensions match exactly
        if (actualWidth === expectedWidth && actualHeight === expectedHeight) {
          // Perfect match - upload directly
          this.performUpload(file);
        } else {
          // Need to crop/resize - show cropper
          this.showCropperModal(file, e.target.result, expectedWidth, expectedHeight);
        }
      };
      img.onerror = () => {
        this.showError('Failed to load image');
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  },

  performUpload (file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', this.currentAssetType);

    this.showLoading(this.currentRow, 'Uploading...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/upload`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      success: (response) => {
        if (response.success) {
          this.showSuccess('Asset uploaded successfully');
          this.refreshAssetRow(this.currentRow, response.data);
          // Trigger custom event for other components to react to asset changes
          console.log('🔔 Triggering asset-uploaded event', response.data);
          $(document).trigger('asset-uploaded', { asset: response.data });
          this.reloadIfOthersWereCut();
        } else {
          this.showError(response.message || 'Upload failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  // The row's size is a minimum that is never enlarged (the Poster, 2000 x 3000).
  isStrictRow () {
    return !!(this.currentRow && String(this.currentRow.data('strict')) === '1');
  },

  // A strict size must be cut from at least that many pixels of the picture.
  cropTooSmall (targetWidth, targetHeight) {
    if (!this.isStrictRow()) {
      return false;
    }
    const box = this.currentCropper.getData();
    if (box.width >= targetWidth - 1 && box.height >= targetHeight - 1) {
      return false;
    }
    this.showError(`This crop is ${Math.round(box.width)} × ${Math.round(box.height)} pixels of the picture. It needs at least ${targetWidth} × ${targetHeight}. Zoom out or make the crop box larger.`);
    return true;
  },

  // The Poster (2000 x 3000) upload also cuts the 1200 x 1600 and Bloodstream posters
  // on the server: reload so their rows show them.
  reloadIfOthersWereCut () {
    if (this.currentAssetType === 'studiodome_poster_2x3') {
      setTimeout(() => window.location.reload(), 800);
    }
  },

  createCropModal () {
    // Create the modal backdrop if it doesn't exist
    if (!document.getElementById('asset-crop-modal-backdrop')) {
      const backdrop = document.createElement('div');
      backdrop.id = 'asset-crop-modal-backdrop';
      backdrop.style.display = 'none';
      document.body.appendChild(backdrop);
    }

    // Create the modal structure if it doesn't exist
    if (!document.getElementById('asset-crop-modal')) {
      const modalHtml = `
          <div class="card-modal large theme-dark" id="asset-crop-modal" style="display: none;">
            <div class="card" style="background: #000;">
              <div class="card-header">
                <div class="header-middle">
                  <h2 class="card-title" id="asset-crop-modal-title">Crop Image</h2>
                </div>
                <button class="close-modal" aria-label="Close modal" type="button">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="card-body" id="asset-crop-modal-body">
                <!-- Cropper image will be inserted here -->
              </div>
              <div class="card-footer" id="asset-crop-modal-controls">
                <!-- Crop controls will be inserted here -->
              </div>
            </div>
          </div>
        `;
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      console.log('[VisualAssetsManager] Created crop modal dynamically');
    }
  },

  showCropperModal (file, imageDataUrl, targetWidth, targetHeight) {
    // Calculate aspect ratio
    const aspectRatio = targetWidth / targetHeight;

    // Get or create modal elements
    let modal = document.getElementById('asset-crop-modal');

    // If modal doesn't exist, create it dynamically
    if (!modal) {
      console.log('[VisualAssetsManager] Crop modal not found in DOM, creating dynamically');
      this.createCropModal();
      modal = document.getElementById('asset-crop-modal');
    }

    const modalTitle = document.getElementById('asset-crop-modal-title');
    const modalBody = document.getElementById('asset-crop-modal-body');
    const modalControls = document.getElementById('asset-crop-modal-controls');

    if (!modal) {
      this.showError('Failed to create crop modal');
      return;
    }

    // Destroy existing cropper if any
    if (this.currentCropper) {
      this.currentCropper.destroy();
      this.currentCropper = null;
    }

    // Clear modal body and controls
    modalBody.innerHTML = '';
    modalControls.innerHTML = '';

    // Set modal title
    modalTitle.textContent = `Crop to ${targetWidth} × ${targetHeight}`;

    // Create image element
    const img = document.createElement('img');
    img.src = imageDataUrl;
    img.id = 'asset-crop-image';
    img.style.maxWidth = '100%';
    img.style.display = 'block';
    modalBody.appendChild(img);

    // Create controls (wrapped in footer-right for card-footer layout)
    modalControls.innerHTML = `
                <div class="footer-right">
                    <button type="button" class="button secondary close-modal" id="asset-crop-cancel">
                        <i class="fas fa-times"></i>
                        Cancel
                    </button>
                    <button type="button" class="button success" id="asset-crop-save">
                        <i class="fas fa-check"></i>
                        Crop & Upload
                    </button>
                </div>
            `;

    // Show backdrop and modal - no animation
    const backdrop = document.getElementById('asset-crop-modal-backdrop');
    if (backdrop) {
      backdrop.style.display = 'block';
    }
    modal.classList.add('show');
    modal.style.display = 'block';

    // Initialize Cropper.js
    this.currentCropper = new Cropper(img, {
      aspectRatio: aspectRatio,
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      background: false,
      zoomable: true,
      rotatable: true,
      cropBoxResizable: true,
      cropBoxMovable: true,
      guides: true,
      center: true,
      highlight: true,
      dragMode: 'move',
    });

    // Handle crop & save
    const saveHandler = () => {
      if (!this.currentCropper) {
        this.showError('No image to crop');
        return;
      }
      if (this.cropTooSmall(targetWidth, targetHeight)) {
        return;
      }

      const canvas = this.currentCropper.getCroppedCanvas({
        width: targetWidth,
        height: targetHeight,
        fillColor: '#000',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!canvas) {
        this.showError('Failed to crop image');
        return;
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          this.showError('Failed to process image');
          return;
        }

        // Create a file from the blob
        const croppedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });

        // Close modal
        this.closeCropModal();

        // Upload the cropped image
        this.performUpload(croppedFile);
      }, file.type, 0.95);
    };

    document.getElementById('asset-crop-save').addEventListener('click', saveHandler);

    // Handle cancel button
    document.getElementById('asset-crop-cancel').addEventListener('click', () => {
      this.closeCropModal();
    });

    // Handle close button in header
    $(modal).find('.close-modal').off('click').on('click', () => {
      this.closeCropModal();
    });
  },

  closeCropModal () {
    const modal = document.getElementById('asset-crop-modal');
    const backdrop = document.getElementById('asset-crop-modal-backdrop');

    if (modal) {
      modal.classList.remove('show');
      modal.style.display = 'none';
    }

    if (backdrop) {
      backdrop.style.display = 'none';
    }

    if (this.currentCropper) {
      this.currentCropper.destroy();
      this.currentCropper = null;
    }
  },

  showTMDBSelector () {
    if (!this.currentAssetType) {
      this.showError('Asset type not selected');
      return;
    }

    this.showLoading(this.currentRow, 'Loading IMDB/TMDB images...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/tmdb-images`,
      method: 'GET',
      data: { type: this.currentAssetType },
      success: (response) => {
        if (response.success && response.data && response.data.length > 0) {
          this.displayTMDBImages(response.data);
        } else {
          this.showError('No IMDB/TMDB images found for this asset type');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Failed to load IMDB/TMDB images';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  displayTMDBImages (images) {
    if (!images || images.length === 0) {
      this.showError('No images available from IMDB/TMDB');
      return;
    }

    // Create backdrop and modal HTML
    let backdropHtml = `<div class="modal-backdrop-fullblack show" id="tmdb-modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #000; z-index: 99999;"></div>`;
    let modalHtml = `
                <div class="card-modal large theme-dark show" id="tmdb-images-modal">
                    <div class="card">
                        <div class="card-header">
                            <div class="header-middle">
                                <h3 class="card-title">Select Image from IMDB/TMDB</h3>
                            </div>
                            <button class="close-modal" aria-label="Close modal" type="button">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-body scrollable">
                            <p class="text-muted mb-3">Click an image to select it for this asset</p>
                            <div class="grid-x grid-margin-x small-up-2 medium-up-3 large-up-4" id="tmdb-images-grid">
            `;

    images.forEach((image, index) => {
      modalHtml += `
                                <div class="cell">
                                    <div class="tmdb-image-option" data-url="${image.url}" style="cursor: pointer; border: 2px solid transparent; transition: all 0.2s;">
                                        <img src="${image.url}" alt="TMDB Image ${index + 1}"
                                             style="width: 100%; border-radius: 4px;">
                                        <small class="text-muted d-block text-center mt-1">
                                            ${image.width} × ${image.height}
                                        </small>
                                    </div>
                                </div>
                `;
    });

    modalHtml += `
                            </div>
                        </div>
                    </div>
                </div>
            `;

    // Remove existing modal if any
    $('#tmdb-images-modal').remove();
    $('#tmdb-modal-backdrop').remove();

    // Add backdrop and modal to page
    $('body').append(backdropHtml).append(modalHtml);

    const $modal = $('#tmdb-images-modal');
    const $backdrop = $('#tmdb-modal-backdrop');

    // Get expected dimensions from row data attributes (set by PHP)
    const expectedWidth = parseInt(this.currentRow.data('expected-width'));
    const expectedHeight = parseInt(this.currentRow.data('expected-height'));

    // Close modal function
    const closeModal = () => {
      $modal.removeClass('show');
      $backdrop.removeClass('show');
      setTimeout(() => {
        $modal.remove();
        $backdrop.remove();
      }, 300);
    };

    // Bind close button
    $modal.find('.close-modal').on('click', closeModal);
    $backdrop.on('click', closeModal);

    // Bind click events
    $('.tmdb-image-option').on('click', (e) => {
      const $option = $(e.currentTarget);
      const tmdbUrl = $option.data('url');
      const imgDimText = $option.find('small').text().trim();
      const dimMatch = imgDimText.match(/(\d+)\s*×\s*(\d+)/);

      closeModal();

      if (!dimMatch || !expectedWidth || !expectedHeight) {
        // Can't determine dimensions, proceed with direct selection
        this.selectTMDBImage(tmdbUrl, null, null);
        return;
      }

      const actualWidth = parseInt(dimMatch[1]);
      const actualHeight = parseInt(dimMatch[2]);

      // Always pass target dimensions to server - let server handle cropping if needed
      this.selectTMDBImage(tmdbUrl, expectedWidth, expectedHeight);
    });

    // Hover effects
    $('.tmdb-image-option').on('mouseenter', function() {
      $(this).css('border-color', '#0a7aff');
    }).on('mouseleave', function() {
      $(this).css('border-color', 'transparent');
    });
  },

  selectTMDBImage (tmdbUrl, targetWidth, targetHeight) {
    this.showLoading(this.currentRow, 'Selecting image...');

    const requestData = {
      type: this.currentAssetType,
      tmdb_url: tmdbUrl,
    };

    // Include target dimensions if provided - server will crop if needed
    if (targetWidth && targetHeight) {
      requestData.target_width = targetWidth;
      requestData.target_height = targetHeight;
    }

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/tmdb-select`,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(requestData),
      success: (response) => {
        if (response.success) {
          this.showSuccess('Image selected successfully');
          this.refreshAssetRow(this.currentRow, response.data);
        } else {
          this.showError(response.message || 'Selection failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Selection failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  deleteAsset (assetId, assetType) {
    if (!confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    this.showLoading(this.currentRow, 'Deleting...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/${assetId}`,
      method: 'DELETE',
      success: (response) => {
        if (response.success) {
          this.showSuccess('Asset deleted successfully');
          this.refreshAssetRow(this.currentRow, null);
          // Trigger custom event for other components to react to asset changes
          console.log('🔔 Triggering asset-deleted event', { assetId: assetId, assetType: assetType });
          $(document).trigger('asset-deleted', { assetId: assetId, assetType: assetType });
        } else {
          this.showError(response.message || 'Delete failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Delete failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  generateActionButtons (assetType, assetData, expectedWidth, expectedHeight, $row) {
    if (!assetData || !assetData.id) {
      // No asset data, return just Manage dropdown (single button, no group)
      return `
                    <div class="button-dropdown">
                        <button class="button small success">
                            <i class="fas fa-cog"></i> Manage <i class="fas fa-chevron-down"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button class="dropdown-item asset-upload-btn" data-type="${assetType}">
                                <i class="fas fa-upload"></i> Upload
                            </button>
                            <button class="dropdown-item asset-tmdb-btn" data-type="${assetType}">
                                <i class="fas fa-film"></i> Import from TMDB
                            </button>
                        </div>
                    </div>
                `;
    }

    // Get dimensions from metadata or assume we need to show all buttons
    const actualWidth = assetData.width || expectedWidth;
    const actualHeight = assetData.height || expectedHeight;

    // Check if dimensions match expected
    const dimensionsMatch = expectedWidth && expectedHeight && actualWidth && actualHeight
      ? (actualWidth === expectedWidth && actualHeight === expectedHeight)
      : true;

    const actualRatio = actualWidth / actualHeight;
    const expectedRatio = expectedWidth / expectedHeight;
    const aspectRatioMatches = Math.abs(actualRatio - expectedRatio) < 0.01;

    const showCrop = !dimensionsMatch && !aspectRatioMatches;
    const showResize = !dimensionsMatch && aspectRatioMatches;

    // Get file extension
    let ext = 'jpg';
    if (assetData.filename) {
      const match = assetData.filename.match(/\.([^.]+)$/);
      if (match) {
        ext = match[1];
      }
    }

    // Map asset type to short name
    const typeShortNames = {
      'bloodstream_poster': 'poster',
      'bloodstream_banner': 'banner',
      'bloodstream_backdrop': 'backdrop',
      'bloodstream_treatment': 'tt',
      'studiodome_poster_2x3': 'poster',
      'studiodome_poster': 'poster',
      'studiodome_banner': 'banner',
      'studiodome_still': 'still',
    };
    const typeShort = typeShortNames[assetType] || 'asset';

    // Get studio ID from page
    const studioId = $row.data('studio-id') || `SD_${this.submissionId}`;

    // Build filename using dimensions
    const actualDimensions = `${actualWidth}x${actualHeight}`;
    const downloadFilename = `${studioId}-${typeShort}-${actualDimensions}.${ext}`;
    const downloadUrl = `/download-proxy?file=${encodeURIComponent(assetData.url)}&name=${encodeURIComponent(downloadFilename)}`;

    // Asset exists - use button-dropdown-group with Manage, Edit, Download, Delete
    let actionsHtml = `
                <div class="button-dropdown-group">
                    <!-- Manage Dropdown -->
                    <div class="button-dropdown">
                        <button class="button small success">
                            <i class="fas fa-cog"></i> Manage <i class="fas fa-chevron-down"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button class="dropdown-item asset-upload-btn" data-type="${assetType}">
                                <i class="fas fa-upload"></i> Upload
                            </button>
                            <button class="dropdown-item asset-tmdb-btn" data-type="${assetType}">
                                <i class="fas fa-film"></i> Import from TMDB
                            </button>
                            <button class="dropdown-item asset-copy-btn"
                                data-asset-id="${assetData.id}"
                                data-type="${assetType}">
                                <i class="fas fa-copy"></i> Copy From
                            </button>
                        </div>
                    </div>

                    <!-- Edit Dropdown -->
                    <div class="button-dropdown">
                        <button class="button small secondary">
                            <i class="fas fa-edit"></i> Edit <i class="fas fa-chevron-down"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button class="dropdown-item asset-manual-crop-btn"
                                data-asset-id="${assetData.id}"
                                data-type="${assetType}"
                                data-expected-width="${expectedWidth}"
                                data-expected-height="${expectedHeight}">
                                <i class="fas fa-crop-alt"></i> Manual Crop
                            </button>
                            <button class="dropdown-item asset-magic-btn"
                                data-asset-id="${assetData.id}"
                                data-type="${assetType}"
                                data-expected-width="${expectedWidth}"
                                data-expected-height="${expectedHeight}">
                                <i class="fas fa-magic"></i> Magic
                            </button>
                            <button class="dropdown-item asset-upscale-btn"
                                data-asset-id="${assetData.id}">
                                <i class="fas fa-expand-arrows-alt"></i> Upscale
                            </button>
                        </div>
                    </div>

                    <!-- Download Button -->
                    <a href="${downloadUrl}"
                       class="button small primary"
                       title="Download asset"
                       download>
                        <i class="fas fa-download"></i>
                    </a>

                    <!-- Delete Button -->
                    <button class="button small alert asset-delete-btn"
                        data-asset-id="${assetData.id}"
                        data-type="${assetType}"
                        title="Delete asset">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>`;

    return actionsHtml;
  },

  checkAndPromptMagicProcessing (assetData, expectedWidth, expectedHeight, $row) {
    // Only prompt if we have valid asset data and dimensions
    if (!assetData || !assetData.id || !expectedWidth || !expectedHeight) {
      return;
    }

    // Get actual dimensions from asset data
    const actualWidth = assetData.width || assetData.metadata?.width || 0;
    const actualHeight = assetData.height || assetData.metadata?.height || 0;

    if (!actualWidth || !actualHeight) {
      return;
    }

    // Check if dimensions match
    const dimensionsMatch = (actualWidth === expectedWidth && actualHeight === expectedHeight);

    if (!dimensionsMatch) {
      // Dimensions don't match - prompt user for magic processing
      const message = `The image dimensions (${actualWidth}×${actualHeight}) don't match the required dimensions (${expectedWidth}×${expectedHeight}).\n\nWould you like to run Magic Processing to automatically fix this?`;

      if (confirm(message)) {
        // User wants to run magic processing
        this.performMagicProcessing(assetData.id, expectedWidth, expectedHeight, $row);
      }
    }
  },

  performMagicProcessing (assetId, targetWidth, targetHeight, $row = null) {
    // Use provided $row or fall back to this.currentRow
    const $targetRow = $row || this.currentRow;

    this.showLoading($targetRow, 'Processing...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/${assetId}/magic`,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        target_width: targetWidth,
        target_height: targetHeight,
      }),
      timeout: 180000, // 3 minutes timeout
      success: (response) => {
        if (response.success) {
          this.showSuccess('Magic processing completed successfully');
          // Don't prompt again after magic processing completes (skipMagicPrompt = true)
          this.refreshAssetRow($targetRow, response.data, true);
        } else {
          this.showError(response.message || 'Magic processing failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Magic processing failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading($targetRow);
      },
    });
  },

  refreshAssetRow ($row, assetData, skipMagicPrompt = false) {
    if (!$row || $row.length === 0) {
      // Refresh entire page as fallback
      location.reload();
      return;
    }

    // Get asset type from row data
    const assetType = $row.data('asset-type');

    // Get status & dimensions cell (now column 2 = index 1)
    const $statusCell = $row.find('td').eq(1);

    // Get expected dimensions from row data attributes (set by PHP)
    let expectedWidth = parseInt($row.data('expected-width'));
    let expectedHeight = parseInt($row.data('expected-height'));

    // Validate we got the dimensions
    if (!expectedWidth || !expectedHeight) {
      console.warn('[VisualAssetsManager] Expected dimensions not found on row', $row);
      // Fallback to parsing (should not happen but kept for safety)
      const $specCell = $row.find('td').eq(0);
      const cellText = $statusCell.text();
      const needMatch = cellText.match(/Need:\s*(\d+)\s*×\s*(\d+)/);
      const expectedMatch = cellText.match(/Expected:\s*(\d+)\s*×\s*(\d+)/);

      if (needMatch) {
        expectedWidth = parseInt(needMatch[1]);
        expectedHeight = parseInt(needMatch[2]);
      } else if (expectedMatch) {
        expectedWidth = parseInt(expectedMatch[1]);
        expectedHeight = parseInt(expectedMatch[2]);
      }
    }

    // Update preview cell
    const $previewCell = $row.find('td').eq(2);
    if (assetData && assetData.url) {
      const source = assetData.metadata && assetData.metadata.source ? assetData.metadata.source : 'unknown';
      const imgId = `asset-img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Add cache busting parameter to ensure browser loads new image
      const cacheBustUrl = assetData.url.includes('?')
        ? `${assetData.url}&_cb=${Date.now()}`
        : `${assetData.url}?_cb=${Date.now()}`;

      $previewCell.html(`
                    <a href="${assetData.url}" target="_blank" class="asset-preview-link">
                        <img src="${cacheBustUrl}"
                             id="${imgId}"
                             alt="Asset preview"
                             style="max-height: 80px; max-width: 150px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">
                    </a>
                    <br>
                    <small class="text-muted dimensions-info" data-img-id="${imgId}">
                        Loading dimensions...
                    </small>
                `);

      // Load image to get actual dimensions
      const img = document.getElementById(imgId);
      if (img) {
        img.onload = function() {
          const actualWidth = this.naturalWidth;
          const actualHeight = this.naturalHeight;
          const dimText = `${actualWidth} × ${actualHeight}`;
          const fileSize = assetData.file_size || '';

          // Check if dimensions match expected
          const dimensionsMatch = expectedWidth && expectedHeight
            ? (actualWidth === expectedWidth && actualHeight === expectedHeight)
            : true;

          const $dimInfo = $previewCell.find('.dimensions-info');

          if (dimensionsMatch) {
            // Dimensions match - normal display
            const infoText = fileSize ? `${dimText} • ${fileSize} • ${source}` : `${dimText} • ${source}`;
            $dimInfo.text(infoText);
            $dimInfo.removeClass('text-warning');
          } else {
            // Dimensions don't match - show warning
            const warningIcon = '<i class="fas fa-exclamation-triangle"></i> ';
            const infoText = fileSize
              ? `${warningIcon}${dimText} • ${fileSize} • ${source}`
              : `${warningIcon}${dimText} • ${source}`;
            $dimInfo.html(infoText);
            $dimInfo.addClass('text-warning');
            $dimInfo.attr('title', `Expected: ${expectedWidth}×${expectedHeight}`);
          }

          // Update status & dimensions cell
          if (dimensionsMatch) {
            $statusCell.html(`
                                <span class="label success">
                                     ${actualWidth}×${actualHeight}
                                </span>
                            `);
          } else if (expectedWidth && expectedHeight) {
            $statusCell.html(`
                                <span class="label warning">
                                    <i class="fas fa-exclamation-triangle"></i> ${actualWidth}×${actualHeight}
                                </span>
                                <br>
                                <small class="text-muted">Expected: ${expectedWidth}×${expectedHeight}</small>
                            `);
          } else {
            $statusCell.html(`
                                <span class="label success">
                                    ${actualWidth}×${actualHeight}
                                </span>
                            `);
          }

          // Now that we have actual dimensions, regenerate buttons with actual data
          const $actionsCell = $row.find('td').eq(3);

          // Update assetData with actual loaded dimensions
          const assetDataWithDimensions = Object.assign({}, assetData, {
            width: actualWidth,
            height: actualHeight,
          });

          const actionsHtml = VisualAssetsManager.generateActionButtons(assetType, assetDataWithDimensions, expectedWidth, expectedHeight, $row);
          $actionsCell.html(actionsHtml);

          // Check if dimensions don't match and prompt for magic processing (unless we're skipping)
          if (!skipMagicPrompt) {
            VisualAssetsManager.checkAndPromptMagicProcessing(assetDataWithDimensions, expectedWidth, expectedHeight, $row);
          }
        };
        img.onerror = function() {
          const fileSize = assetData.file_size || 'N/A';
          $previewCell.find('.dimensions-info').text(`${fileSize} • ${source}`);
        };
      }
    } else {
      $previewCell.html(`
                    <div style="background: rgba(255,255,255,0.02); padding: 1.5rem; border-radius: 4px; border: 1px dashed rgba(255,255,255,0.1); text-align: center;">
                        <i class="fas fa-image text-muted opacity-50"></i>
                        <br>
                        <small class="text-muted">No preview</small>
                    </div>
                `);

      // Update status cell for missing asset
      if (expectedWidth && expectedHeight) {
        // Check if this is an optional asset by looking at the first cell (label cell)
        const $labelCell = $row.find('td').eq(0);
        const isOptional = $labelCell.text().includes('(Optional)');

        if (isOptional) {
          // Optional asset - use warning badge
          $statusCell.html(`
                            <span class="label warning">
                                Optional
                            </span>
                            <br>
                            <small class="text-muted">Need: ${expectedWidth}×${expectedHeight}</small>
                        `);
        } else {
          // Required asset - use alert badge
          $statusCell.html(`
                            <span class="label alert">
                                Missing
                            </span>
                            <br>
                            <small class="text-muted">Need: ${expectedWidth}×${expectedHeight}</small>
                        `);
        }
      }
    }

    // Update actions cell - generate buttons immediately with available data
    const $actionsCell = $row.find('td').eq(3);
    const actionsHtml = this.generateActionButtons(assetType, assetData, expectedWidth, expectedHeight, $row);
    $actionsCell.html(actionsHtml);
  },

  refreshResourcesCard () {
    // Refresh the entire resources card (videos, documents, subtitles) without full page reload
    if (!this.submissionId) {
      console.error('[VisualAssetsManager] Cannot refresh resources card: submission ID not found');
      console.log('[VisualAssetsManager] Falling back to page reload');
      location.reload();
      return;
    }

    console.log('[VisualAssetsManager] Refreshing resources card for submission:', this.submissionId);

    // Save scroll position
    this.savedScrollPosition = $(window).scrollTop();

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/resources-card`,
      method: 'GET',
      dataType: 'json',
      success: (response) => {
        if (response.success && response.html) {
          // Find the resources card
          const $card = $('#visual-assets-section');

          if ($card.length > 0) {
            // Clean up old FileUpload instances before DOM replacement (only within this card)
            this.cleanupFileUploads($card[0]);

            // Replace the card content with updated HTML
            const $newCard = $(response.html);

            // Preserve the card's position and replace it
            $card.replaceWith($newCard);

            // Reinitialize event bindings and image dimensions
            this.bindEvents();
            this.loadImageDimensions();
            this.initFileUploads();

            // Restore scroll position
            $(window).scrollTop(this.savedScrollPosition);

            console.log('[VisualAssetsManager] Resources card refreshed successfully');
          } else {
            console.warn('[VisualAssetsManager] Resources card not found in DOM, falling back to page reload');
            location.reload();
          }
        } else {
          console.error('[VisualAssetsManager] Failed to get updated card HTML. Response:', response);
          console.error('[VisualAssetsManager] response.success:', response?.success, 'response.html length:', response?.html?.length);
          // Reload to show updated state
          location.reload();
        }
      },
      error: (xhr) => {
        console.error('[VisualAssetsManager] AJAX error refreshing resources card:', xhr.status, xhr.statusText);
        console.error('[VisualAssetsManager] Response text (first 1000 chars):', xhr.responseText?.substring(0, 1000));
        // Reload to show updated state
        location.reload();
      },
    });
  },

  showLoading ($row, message = 'Processing...') {
    if ($row && $row.length > 0) {
      this.activeOperations++;

      // Use the new row overlay system
      if (typeof window.showAssetRowOverlay === 'function') {
        window.showAssetRowOverlay($row, message, 'loading');
      } else {
        // Fallback if overlay function not available
        const $statusCell = $row.find('td').eq(1);
        if (!$row.data('original-status')) {
          $row.data('original-status', $statusCell.html());
        }
        $statusCell.html(`
            <span class="label secondary">
              <i class="fas fa-spinner fa-spin"></i> ${message}
            </span>
          `);
        $row.find('button').prop('disabled', true);
      }
    }
  },

  hideLoading ($row) {
    if ($row && $row.length > 0) {
      this.activeOperations--;

      // Use the new row overlay system
      if (typeof window.hideAssetRowOverlay === 'function') {
        window.hideAssetRowOverlay($row);
      } else {
        // Fallback if overlay function not available
        const originalStatus = $row.data('original-status');
        if (originalStatus) {
          const $statusCell = $row.find('td').eq(1);
          $statusCell.html(originalStatus);
          $row.removeData('original-status');
        }
        $row.find('button').prop('disabled', false);
      }
    }
  },

  showSuccess (message) {
    if (window.StudioDome && window.StudioDome.notify) {
      window.StudioDome.notify(message, {
        type: 'success',
        icon: 'fa-check-circle',
        duration: 3000,
      });
    } else {
      console.log('Success:', message);
    }
  },

  showError (message) {
    // Silently skip "Asset not found" errors - these are expected when no asset exists yet
    if (message && message.toLowerCase().includes('asset not found')) {
      console.log('Asset not found (expected, not showing notification)');
      return;
    }

    if (window.StudioDome && window.StudioDome.notify) {
      window.StudioDome.notify(message, {
        type: 'error',
        icon: 'fa-times-circle',
        duration: 0,
      });
    } else {
      alert('Error: ' + message);
    }
  },

  openModalWithoutScroll ($modal) {
    // Save current scroll position
    this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Open the modal
    $modal.foundation('open');

    // Restore scroll position after modal opens (prevents initial jump)
    // Use setTimeout to ensure it runs after Foundation's logic
    setTimeout(() => {
      window.scrollTo(0, this.savedScrollPosition);
    }, 0);
  },

  showUpscaleModal (assetId) {
    const backdropHtml = `<div class="modal-backdrop-fullblack show" id="upscale-modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #000; z-index: 99999;"></div>`;
    const html = `
                <div class="card-modal small theme-dark show" id="upscale-modal" style="background: #000;">
                    <div class="card" style="background: #000;">
                        <div class="card-header">
                            <div class="header-middle">
                                <h3 class="card-title">Upscale Image with AI</h3>
                            </div>
                            <button class="close-modal" aria-label="Close modal" type="button">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-body">
                            <p class="text-muted">Uses Real-ESRGAN AI model to enhance and upscale the image</p>
                            <div class="stack-sm">
                                <label>
                                    Upscale Factor
                                    <select id="upscale-factor" class="input-base">
                                        <option value="2">2x (Recommended)</option>
                                        <option value="3">3x</option>
                                        <option value="4">4x (Best Quality)</option>
                                    </select>
                                    <small class="text-muted">Higher factors take longer but produce better quality</small>
                                </label>
                                <label>
                                    <input type="checkbox" id="face-enhance">
                                    Enable Face Enhancement (GFPGAN)
                                    <small class="text-muted">Improves facial details in images with people</small>
                                </label>
                                <p class="callout warning" style="font-size: 0.9rem;">
                                    <i class="fas fa-info-circle"></i> This process may take 30-60 seconds. The page will update automatically when complete.
                                </p>
                            </div>
                        </div>
                        <div class="card-footer">
                            <button class="button secondary close-modal-btn">Cancel</button>
                            <button class="button success" id="upscale-confirm">
                                <i class="fas fa-magic"></i> Upscale Now
                            </button>
                        </div>
                    </div>
                </div>
            `;

    $('#upscale-modal').remove();
    $('#upscale-modal-backdrop').remove();
    $('body').append(backdropHtml).append(html);

    const $modal = $('#upscale-modal');
    const $backdrop = $('#upscale-modal-backdrop');

    const closeModal = () => {
      $modal.removeClass('show');
      $backdrop.removeClass('show');
      setTimeout(() => {
        $modal.remove();
        $backdrop.remove();
      }, 300);
    };

    $('#upscale-confirm').on('click', (e) => {
      e.preventDefault();
      const scale = parseInt($('#upscale-factor').val());
      const faceEnhance = $('#face-enhance').is(':checked');

      closeModal();
      this.performUpscale(assetId, scale, faceEnhance);
    });

    $modal.find('.close-modal').on('click', closeModal);
    $modal.find('.close-modal-btn').on('click', closeModal);
    $backdrop.on('click', closeModal);
  },

  performUpscale (assetId, scale, faceEnhance) {
    this.showLoading(this.currentRow, `Upscaling ${scale}x (this may take 30-60 seconds)...`);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/${assetId}/upscale`,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        scale: scale,
        face_enhance: faceEnhance,
      }),
      timeout: 120000, // 2 minutes timeout
      success: (response) => {
        if (response.success) {
          this.showSuccess(`Asset upscaled ${scale}x successfully!`);
          this.refreshAssetRow(this.currentRow, response.data);
        } else {
          this.showError(response.message || 'Upscale failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upscale failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  showCropperModalForExistingAsset (imageDataUrl, targetWidth, targetHeight, assetId) {
    const aspectRatio = targetWidth / targetHeight;

    // Get or create modal elements
    let modal = document.getElementById('asset-crop-modal');

    // If modal doesn't exist, create it dynamically
    if (!modal) {
      console.log('[VisualAssetsManager] Crop modal not found in DOM for existing asset, creating dynamically');
      this.createCropModal();
      modal = document.getElementById('asset-crop-modal');
    }

    const modalTitle = document.getElementById('asset-crop-modal-title');
    const modalBody = document.getElementById('asset-crop-modal-body');
    const modalControls = document.getElementById('asset-crop-modal-controls');

    if (!modal) {
      this.showError('Failed to create crop modal');
      return;
    }

    // Destroy existing cropper if any
    if (this.currentCropper) {
      this.currentCropper.destroy();
      this.currentCropper = null;
    }

    modalBody.innerHTML = '';
    modalControls.innerHTML = '';

    modalTitle.textContent = `Crop and Resize to ${targetWidth} × ${targetHeight}`;

    const img = document.createElement('img');
    img.src = imageDataUrl;
    img.id = 'asset-crop-image';
    img.style.maxWidth = '100%';
    img.style.display = 'block';
    modalBody.appendChild(img);

    modalControls.innerHTML = `
                <div class="footer-right">
                    <button type="button" class="button secondary close-modal" id="asset-crop-cancel">
                        <i class="fas fa-times"></i>
                        Cancel
                    </button>
                    <button type="button" class="button success" id="asset-crop-save">
                        <i class="fas fa-check"></i>
                        Crop & Resize
                    </button>
                </div>
            `;

    // Show backdrop and modal - no animation
    const backdrop = document.getElementById('asset-crop-modal-backdrop');
    if (backdrop) {
      backdrop.style.display = 'block';
    }
    modal.classList.add('show');
    modal.style.display = 'block';

    this.currentCropper = new Cropper(img, {
      aspectRatio: aspectRatio,
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      background: false,
      zoomable: true,
      rotatable: true,
      cropBoxResizable: true,
      cropBoxMovable: true,
      guides: true,
      center: true,
      highlight: true,
      dragMode: 'move',
    });

    const saveHandler = () => {
      if (!this.currentCropper) {
        this.showError('No image to crop');
        return;
      }
      if (this.cropTooSmall(targetWidth, targetHeight)) {
        return;
      }

      const canvas = this.currentCropper.getCroppedCanvas({
        width: targetWidth,
        height: targetHeight,
        fillColor: '#000',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!canvas) {
        this.showError('Failed to crop image');
        return;
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          this.showError('Failed to process image');
          return;
        }

        // Close modal
        this.closeCropModal();

        // Upload the cropped and resized image
        this.uploadCroppedAsset(blob, assetId);
      }, 'image/jpeg', 0.95);
    };

    document.getElementById('asset-crop-save').addEventListener('click', saveHandler);

    // Handle cancel button
    document.getElementById('asset-crop-cancel').addEventListener('click', () => {
      this.closeCropModal();
    });

    // Handle close button in header
    $(modal).find('.close-modal').off('click').on('click', () => {
      this.closeCropModal();
    });
  },

  uploadCroppedAsset (blob, assetId) {
    const formData = new FormData();
    const filename = 'cropped-asset-' + Date.now() + '.jpg';
    formData.append('file', blob, filename);
    formData.append('type', this.currentAssetType);

    this.showLoading(this.currentRow, 'Uploading cropped image...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/upload`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      success: (response) => {
        if (response.success) {
          this.showSuccess('Cropped asset uploaded successfully');
          this.refreshAssetRow(this.currentRow, response.data);
          // Trigger custom event for other components to react to asset changes
          console.log('🔔 Triggering asset-uploaded event (cropped)', response.data);
          $(document).trigger('asset-uploaded', { asset: response.data });
          this.reloadIfOthersWereCut();
        } else {
          this.showError(response.message || 'Upload failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  showResizeModal (assetId, expectedWidth, expectedHeight) {
    // Use provided dimensions (from button data attributes) or default to 1920x1080
    const defaultWidth = expectedWidth || 1920;
    const defaultHeight = expectedHeight || 1080;

    const backdropHtml = `<div class="modal-backdrop-fullblack show" id="resize-modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #000; z-index: 99999;"></div>`;
    const html = `
                <div class="card-modal small theme-dark show" id="resize-modal" style="background: #000;">
                    <div class="card" style="background: #000;">
                        <div class="card-header">
                            <div class="header-middle">
                                <h3 class="card-title">Resize Image</h3>
                            </div>
                            <button class="close-modal" aria-label="Close modal" type="button">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-body">
                            <p class="text-muted">Adjust image dimensions to match requirements</p>
                            <div class="stack-sm">
                                <div class="grid-x grid-margin-x">
                                    <div class="cell medium-6">
                                        <label>
                                            Width (px)
                                            <input type="number" id="resize-width" class="input-base" value="${defaultWidth}" min="1" max="10000">
                                        </label>
                                    </div>
                                    <div class="cell medium-6">
                                        <label>
                                            Height (px)
                                            <input type="number" id="resize-height" class="input-base" value="${defaultHeight}" min="1" max="10000">
                                        </label>
                                    </div>
                                </div>
                                <label>
                                    <input type="checkbox" id="maintain-aspect-ratio" checked>
                                    Maintain Aspect Ratio
                                    <small class="text-muted">Prevents image distortion by maintaining proportions</small>
                                </label>
                            </div>
                        </div>
                        <div class="card-footer">
                            <button class="button secondary close-modal-btn">Cancel</button>
                            <button class="button success" id="resize-confirm">
                                <i class="fas fa-compress-alt"></i> Resize
                            </button>
                        </div>
                    </div>
                </div>
            `;

    $('#resize-modal').remove();
    $('#resize-modal-backdrop').remove();
    $('body').append(backdropHtml).append(html);

    const $modal = $('#resize-modal');
    const $backdrop = $('#resize-modal-backdrop');

    const closeModal = () => {
      $modal.removeClass('show');
      $backdrop.removeClass('show');
      setTimeout(() => {
        $modal.remove();
        $backdrop.remove();
      }, 300);
    };

    $('#resize-confirm').on('click', (e) => {
      e.preventDefault();
      const width = parseInt($('#resize-width').val());
      const height = parseInt($('#resize-height').val());
      const maintainAspectRatio = $('#maintain-aspect-ratio').is(':checked');

      if (!width || !height || width < 1 || height < 1) {
        VisualAssetsManager.showError('Please enter valid dimensions');
        return;
      }

      closeModal();
      this.performResize(assetId, width, height, maintainAspectRatio);
    });

    $modal.find('.close-modal').on('click', closeModal);
    $modal.find('.close-modal-btn').on('click', closeModal);
    $backdrop.on('click', closeModal);
  },

  performResize (assetId, width, height, maintainAspectRatio) {
    this.showLoading(this.currentRow, `Resizing to ${width}×${height}...`);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/${assetId}/resize`,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        width: width,
        height: height,
        maintain_aspect_ratio: maintainAspectRatio,
      }),
      success: (response) => {
        if (response.success) {
          this.showSuccess(`Asset resized to ${width}×${height} successfully!`);
          this.refreshAssetRow(this.currentRow, response.data);
        } else {
          this.showError(response.message || 'Resize failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Resize failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  showCopyAssetModal (targetType) {
    // Get all rows to find source assets
    const $allRows = $('#visual-assets-section tbody tr');
    let sourceOptions = [];

    $allRows.each((index, row) => {
      const $row = $(row);
      const sourceType = $row.data('asset-type');
      // Check for success badge in the status & dimensions column (now column 2 = index 1)
      const $statusBadge = $row.find('td:nth-child(2) .label.success, td:nth-child(2) .label.warning');

      // Only show rows that have assets (status = provided/warning) and are not the target type
      if (sourceType !== targetType && $statusBadge.length > 0) {
        const $label = $row.find('td:first strong');
        const label = $label.text();
        sourceOptions.push({
          type: sourceType,
          label: label,
        });
      }
    });

    if (sourceOptions.length === 0) {
      this.showError('No other assets available to copy from');
      return;
    }

    // Create backdrop and modal HTML
    let backdropHtml = `<div class="modal-backdrop-fullblack show" id="copy-modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #000; z-index: 99999;"></div>`;
    let modalHtml = `
                <div class="card-modal small theme-dark show" id="copy-asset-modal">
                    <div class="card">
                        <div class="card-header">
                            <div class="header-middle">
                                <h3 class="card-title">Copy Asset</h3>
                            </div>
                            <button class="close-modal" aria-label="Close modal" type="button">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-body">
                            <p class="text-muted mb-3">Select which asset to copy from</p>
                            <label>
                                Source Asset
                                <select id="copy-source-type" class="input-base">
            `;

    sourceOptions.forEach((option) => {
      modalHtml += `<option value="${option.type}">${option.label}</option>`;
    });

    modalHtml += `
                                </select>
                            </label>
                        </div>
                        <div class="card-footer">
                            <button class="button secondary close-modal-btn">
                                Cancel
                            </button>
                            <button class="button success" id="copy-confirm">
                                <i class="fas fa-copy"></i> Copy Asset
                            </button>
                        </div>
                    </div>
                </div>
            `;

    // Remove existing modal if any
    $('#copy-asset-modal').remove();
    $('#copy-modal-backdrop').remove();

    // Add backdrop and modal to page
    $('body').append(backdropHtml).append(modalHtml);

    const $modal = $('#copy-asset-modal');
    const $backdrop = $('#copy-modal-backdrop');

    // Close modal function
    const closeModal = () => {
      $modal.removeClass('show');
      $backdrop.removeClass('show');
      setTimeout(() => {
        $modal.remove();
        $backdrop.remove();
      }, 300);
    };

    // Bind close buttons
    $modal.find('.close-modal').on('click', closeModal);
    $modal.find('.close-modal-btn').on('click', closeModal);
    $backdrop.on('click', closeModal);

    // Handle copy
    $('#copy-confirm').on('click', (e) => {
      e.preventDefault();
      const sourceType = $('#copy-source-type').val();
      closeModal();
      this.performCopyAsset(sourceType, targetType);
    });
  },

  performCopyAsset (sourceType, targetType) {
    this.showLoading(this.currentRow, 'Copying asset...');

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/assets/copy`,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        source_type: sourceType,
        target_type: targetType,
      }),
      success: (response) => {
        if (response.success) {
          this.showSuccess('Asset copied successfully');
          this.refreshAssetRow(this.currentRow, response.data);
        } else {
          this.showError(response.message || 'Copy failed');
        }
      },
      error: (xhr) => {
        let errorMsg = 'Copy failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        this.showError(errorMsg);
      },
      complete: () => {
        this.hideLoading(this.currentRow);
      },
    });
  },

  uploadVideo (file, type) {
    this.activeOperations++;

    // Find the row for this video type
    const $btn = $(`.video-upload-btn[data-type="${type}"]`);
    const $row = $btn.closest('tr');

    // Show loading overlay on the row
    if (typeof window.showAssetRowOverlay === 'function') {
      window.showAssetRowOverlay($row, 'Uploading video...', 'loading');
    } else {
      this.showSuccess(`Uploading ${type} video...`);
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('submission_id', this.submissionId);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/videos`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      xhr: () => {
        const xhr = new window.XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            if (typeof window.updateAssetRowOverlay === 'function') {
              window.updateAssetRowOverlay($row, `Uploading: ${Math.round(percentComplete)}%`, 'loading');
            } else {
              this.showSuccess(`Uploading: ${Math.round(percentComplete)}%`);
            }
          }
        }, false);
        return xhr;
      },
      success: (response) => {
        if (response.success) {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, 'Uploaded! Refreshing...', 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else {
            this.showSuccess('Video uploaded successfully!');
          }
          // Trigger asset-uploaded event for validation refresh
          console.log('🔔 Triggering asset-uploaded event (video)', response.data);
          $(document).trigger('asset-uploaded', { asset: response.data, type: type });
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, response.message || 'Upload failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Upload failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
      complete: () => {
        this.activeOperations--;
      },
    });
  },

  deleteVideo (uploadId, type) {
    if (!confirm(`Are you sure you want to delete this ${type} video? This action cannot be undone.`)) {
      return;
    }

    // Find the row for this video type
    const $btn = $(`.video-delete-btn[data-upload-id="${uploadId}"]`);
    const $row = $btn.closest('tr');

    // Show loading overlay on the row
    if (typeof window.showAssetRowOverlay === 'function') {
      window.showAssetRowOverlay($row, 'Deleting video...', 'loading');
    } else {
      this.showSuccess('Deleting video...');
    }

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/videos/${uploadId}`,
      method: 'DELETE',
      success: (response) => {
        if (response.success) {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, 'Deleted! Refreshing...', 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else {
            this.showSuccess('Video deleted successfully');
          }
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, response.message || 'Delete failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Delete failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Delete failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
    });
  },

  uploadDocument (file) {
    this.activeOperations++;

    // Find the misc documents table or create a temporary reference
    const $documentsTable = $('.document-delete-btn').first().closest('table');
    let $row;

    if ($documentsTable.length > 0) {
      // Use the first document row for progress display
      $row = $documentsTable.find('tr[data-document-row]').first();
    }

    // Show loading overlay on the row
    if (typeof window.showAssetRowOverlay === 'function' && $row && $row.length > 0) {
      window.showAssetRowOverlay($row, 'Uploading document...', 'loading');
    } else {
      this.showSuccess('Uploading document...');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'document');
    formData.append('submission_id', this.submissionId);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/documents`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      xhr: () => {
        const xhr = new window.XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            if (typeof window.updateAssetRowOverlay === 'function' && $row && $row.length > 0) {
              window.updateAssetRowOverlay($row, `Uploading: ${Math.round(percentComplete)}%`, 'loading');
            } else {
              this.showSuccess(`Uploading: ${Math.round(percentComplete)}%`);
            }
          }
        }, false);
        return xhr;
      },
      success: (response) => {
        if (response.success) {
          if (typeof window.updateAssetRowOverlay === 'function' && $row && $row.length > 0) {
            window.updateAssetRowOverlay($row, 'Uploaded! Refreshing...', 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else {
            this.showSuccess('Document uploaded successfully!');
          }
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          if (typeof window.updateAssetRowOverlay === 'function' && $row && $row.length > 0) {
            window.updateAssetRowOverlay($row, response.message || 'Upload failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Upload failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function' && $row && $row.length > 0) {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
      complete: () => {
        this.activeOperations--;
      },
    });
  },

  deleteDocument (uploadId) {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    const $btn = $(`.document-delete-btn[data-upload-id="${uploadId}"]`);
    const $row = $btn.closest('tr');

    if (typeof window.showAssetRowOverlay === 'function') {
      window.showAssetRowOverlay($row, 'Deleting document...', 'loading');
    } else {
      this.showSuccess('Deleting document...');
    }

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/documents/${uploadId}`,
      method: 'DELETE',
      success: (response) => {
        if (response.success) {
          // Remove the row with a fade-out animation
          $row.fadeOut(300, function() {
            $(this).remove();
            // Check if table is now empty and show empty state if needed
            const $table = $('#documents-table tbody');
            if ($table.find('tr').length === 0) {
              $table.html('<tr><td colspan="5" class="text-center text-muted"><em>No documents uploaded yet</em></td></tr>');
            }
          });
        } else {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, response.message || 'Delete failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Delete failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Delete failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
    });
  },

  uploadAgreement (file) {
    this.activeOperations++;

    // Find the agreement row (BST Agreement type)
    const $row = $('tr[data-document-row][data-document-type="BST Agreement"]');

    // Show loading overlay on the row
    if (typeof window.showAssetRowOverlay === 'function' && $row.length > 0) {
      window.showAssetRowOverlay($row, 'Uploading agreement...', 'loading');
    } else {
      this.showSuccess('Uploading agreement...');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('submission_id', this.submissionId);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/agreement`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      xhr: () => {
        const xhr = new window.XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            if (typeof window.updateAssetRowOverlay === 'function' && $row.length > 0) {
              window.updateAssetRowOverlay($row, `Uploading: ${Math.round(percentComplete)}%`, 'loading');
            } else {
              this.showSuccess(`Uploading: ${Math.round(percentComplete)}%`);
            }
          }
        }, false);
        return xhr;
      },
      success: (response) => {
        if (response.success) {
          if (typeof window.updateAssetRowOverlay === 'function' && $row.length > 0) {
            window.updateAssetRowOverlay($row, 'Uploaded! Refreshing...', 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else {
            this.showSuccess('Agreement uploaded successfully!');
          }
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          if (typeof window.updateAssetRowOverlay === 'function' && $row.length > 0) {
            window.updateAssetRowOverlay($row, response.message || 'Upload failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Upload failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function' && $row.length > 0) {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
      complete: () => {
        this.activeOperations--;
      },
    });
  },

  deleteAgreement (uploadId) {
    if (!confirm('Are you sure you want to delete the Bloodstream agreement? This action cannot be undone.')) {
      return;
    }

    const $btn = $(`.agreement-delete-btn[data-upload-id="${uploadId}"]`);
    const $row = $btn.closest('tr');

    if (typeof window.showAssetRowOverlay === 'function') {
      window.showAssetRowOverlay($row, 'Deleting agreement...', 'loading');
    } else {
      this.showSuccess('Deleting agreement...');
    }

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/agreement/${uploadId}`,
      method: 'DELETE',
      success: (response) => {
        if (response.success) {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, 'Deleted! Refreshing...', 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else {
            this.showSuccess('Agreement deleted successfully');
          }
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, response.message || 'Delete failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Delete failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Delete failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
    });
  },

  uploadSubtitle (file) {
    this.activeOperations++;

    // Detect language from filename
    const language = this.detectSubtitleLanguage(file.name);

    // Find subtitle section to show progress
    // Try to find existing subtitle rows first
    let $subtitlesSection = $('tr[data-subtitle-row]').first().closest('table');
    let $row = $('tr[data-subtitle-row]').first();

    // If no existing subtitle rows, find the subtitle section by the heading
    if (!$row || $row.length === 0) {
      // Look for the subtitle heading specifically (more reliable than button proximity)
      const $subtitleHeading = $('h6:contains("Subtitles")').filter(function() {
        // Make sure it's the subtitles heading, not a different one
        return $(this).text().indexOf('Subtitles') !== -1;
      }).first();

      if ($subtitleHeading.length > 0) {
        // Find the next table or paragraph after the heading
        const $nextTable = $subtitleHeading.nextAll('table').first();
        const $nextP = $subtitleHeading.nextAll('p').first();

        if ($nextTable.length > 0) {
          $subtitlesSection = $nextTable;
        } else if ($nextP.length > 0) {
          // Use the "no subtitles" message paragraph
          $subtitlesSection = $nextP;
        }
      }

      // Fallback: if still nothing found, use the upload button's container
      if (!$subtitlesSection || $subtitlesSection.length === 0) {
        $subtitlesSection = $('.subtitle-upload-btn').closest('div');
      }
    }

    // Show loading state inline in the subtitle section
    let $progressIndicator;
    if ($row && $row.length > 0) {
      // Use existing row overlay for existing subtitles
      if (typeof window.showAssetRowOverlay === 'function') {
        window.showAssetRowOverlay($row, 'Uploading subtitle...', 'loading');
      }
    } else if ($subtitlesSection && $subtitlesSection.length > 0) {
      // Create inline progress indicator for first subtitle upload
      $progressIndicator = $('<div class="subtitle-upload-progress" style="padding: 1rem; background: rgba(100, 181, 246, 0.1); border-radius: 4px; margin: 1rem 0; text-align: center; border: 1px solid rgba(100, 181, 246, 0.3);"><i class="fas fa-spinner fa-spin"></i> <span class="progress-message">Uploading subtitle...</span></div>');
      if ($subtitlesSection.is('table')) {
        $subtitlesSection.before($progressIndicator);
      } else {
        $subtitlesSection.after($progressIndicator);
      }
    } else {
      // Fallback to notification
      this.showSuccess('Uploading subtitle...');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language); // Send detected language
    formData.append('submission_id', this.submissionId);

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/subtitles`,
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      xhr: () => {
        const xhr = new window.XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            const progressMsg = `Uploading: ${Math.round(percentComplete)}%`;

            // Update progress in row overlay or inline indicator
            if ($row && $row.length > 0 && typeof window.updateAssetRowOverlay === 'function') {
              window.updateAssetRowOverlay($row, progressMsg, 'loading');
            } else if ($progressIndicator && $progressIndicator.length > 0) {
              $progressIndicator.find('.progress-message').text(progressMsg);
            }
          }
        }, false);
        return xhr;
      },
      success: (response) => {
        if (response.success) {
          const langInfo = response.language_name ? ` (${response.language_name})` : '';
          const successMsg = `Uploaded${langInfo}! Refreshing...`;

          // Show success state
          if ($row && $row.length > 0 && typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, successMsg, 'success');
            setTimeout(() => window.hideAssetRowOverlay($row), 1000);
          } else if ($progressIndicator && $progressIndicator.length > 0) {
            $progressIndicator
              .css('background', 'rgba(76, 175, 80, 0.1)')
              .css('border-color', 'rgba(76, 175, 80, 0.3)')
              .html(`<i class="fas fa-check-circle" style="color: #4caf50;"></i> <span class="progress-message">${successMsg}</span>`);
            setTimeout(() => $progressIndicator.remove(), 1500);
          } else {
            this.showSuccess(`Subtitle uploaded successfully${langInfo}!`);
          }

          // Refresh the card to show the new subtitle
          setTimeout(() => this.refreshResourcesCard(), 1000);
        } else {
          const errorMsg = response.message || 'Upload failed';

          // Show error state
          if ($row && $row.length > 0 && typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, errorMsg, 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else if ($progressIndicator && $progressIndicator.length > 0) {
            $progressIndicator
              .css('background', 'rgba(244, 67, 54, 0.1)')
              .css('border-color', 'rgba(244, 67, 54, 0.3)')
              .html(`<i class="fas fa-exclamation-circle" style="color: #f44336;"></i> <span class="progress-message">${errorMsg}</span>`);
            setTimeout(() => $progressIndicator.remove(), 3000);
          } else {
            this.showError(errorMsg);
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Upload failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }

        // Show error state
        if ($row && $row.length > 0 && typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else if ($progressIndicator && $progressIndicator.length > 0) {
          $progressIndicator
            .css('background', 'rgba(244, 67, 54, 0.1)')
            .css('border-color', 'rgba(244, 67, 54, 0.3)')
            .html(`<i class="fas fa-exclamation-circle" style="color: #f44336;"></i> <span class="progress-message">${errorMsg}</span>`);
          setTimeout(() => $progressIndicator.remove(), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
      complete: () => {
        this.activeOperations--;
      },
    });
  },

  deleteSubtitle (uploadId, language) {
    const langDisplay = language ? ` (${language})` : '';
    if (!confirm(`Are you sure you want to delete this subtitle${langDisplay}? This action cannot be undone.`)) {
      return;
    }

    const $btn = $(`.subtitle-delete-btn[data-upload-id="${uploadId}"]`);
    const $row = $btn.closest('tr');

    if (typeof window.showAssetRowOverlay === 'function') {
      window.showAssetRowOverlay($row, 'Deleting subtitle...', 'loading');
    } else {
      this.showSuccess('Deleting subtitle...');
    }

    $.ajax({
      url: `/api/admin/submissions/${this.submissionId}/subtitles/${uploadId}`,
      method: 'DELETE',
      success: (response) => {
        if (response.success) {
          // Remove the row with a fade-out animation
          $row.fadeOut(300, function() {
            $(this).remove();
            // Check if table is now empty and show empty state if needed
            const $table = $('#subtitles-table tbody');
            if ($table.find('tr').length === 0) {
              $table.html('<tr><td colspan="5" class="text-center text-muted"><em>No subtitles uploaded yet</em></td></tr>');
            }
          });
        } else {
          if (typeof window.updateAssetRowOverlay === 'function') {
            window.updateAssetRowOverlay($row, response.message || 'Delete failed', 'error');
            setTimeout(() => window.hideAssetRowOverlay($row), 3000);
          } else {
            this.showError(response.message || 'Delete failed');
          }
        }
      },
      error: (xhr) => {
        let errorMsg = 'Delete failed';
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMsg = xhr.responseJSON.message;
        }
        if (typeof window.updateAssetRowOverlay === 'function') {
          window.updateAssetRowOverlay($row, errorMsg, 'error');
          setTimeout(() => window.hideAssetRowOverlay($row), 3000);
        } else {
          this.showError(errorMsg);
        }
      },
    });
  },

  detectSubtitleLanguage (filename) {
    const lower = filename.toLowerCase();

    // Pattern 1: filename.{lang}.ext (e.g., movie.en.srt)
    const pattern1 = lower.match(/\.([a-z]{2})\.(srt|vtt|sub|sbv|ass|ssa)$/);
    if (pattern1) return pattern1[1];

    // Pattern 2: filename_{lang}.ext (e.g., movie_es.srt)
    const pattern2 = lower.match(/_([a-z]{2})\.(srt|vtt|sub|sbv|ass|ssa)$/);
    if (pattern2) return pattern2[1];

    // Pattern 3: filename-{lang}.ext (e.g., movie-fr.srt)
    const pattern3 = lower.match(/-([a-z]{2})\.(srt|vtt|sub|sbv|ass|ssa)$/);
    if (pattern3) return pattern3[1];

    // Pattern 4: filename.{language_name}.ext
    const languageNames = {
      'english': 'en',
      'spanish': 'es',
      'espanol': 'es',
      'french': 'fr',
      'francais': 'fr',
      'german': 'de',
      'deutsch': 'de',
      'italian': 'it',
      'italiano': 'it',
      'portuguese': 'pt',
      'portugues': 'pt',
      'japanese': 'ja',
      'korean': 'ko',
      'chinese': 'zh',
      'arabic': 'ar',
      'russian': 'ru',
      'hindi': 'hi',
    };

    for (const [langName, langCode] of Object.entries(languageNames)) {
      if (lower.includes(`.${langName}.`)) {
        return langCode;
      }
    }

    // Default to 'en' if no language detected
    return 'en';
  },
};

// ============================================
// Shared Dropdown Button Handler
// ============================================
// Note: Row overlay functionality now provided by global RowOverlay utility
// Available as: RowOverlay.show(), RowOverlay.update(), RowOverlay.hide()

// Initialize dropdown buttons (hover-to-open with overflow detection)
function initializeDropdownButtons () {
  $('.button-dropdown').each(function() {
    const $dropdown = $(this);
    const $menu = $dropdown.find('.dropdown-menu');
    let closeTimeout = null;

    // Prevent duplicate initialization
    if ($dropdown.data('dropdown-initialized')) {
      return;
    }
    $dropdown.data('dropdown-initialized', true);

    // Open on hover
    $dropdown.on('mouseenter', function() {
      // Clear any pending close timeout
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }

      $dropdown.addClass('open');

      // Check if dropdown would overflow viewport on the right
      const menuRect = $menu[0].getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // If menu extends beyond right edge, align it to the right
      if (menuRect.right > viewportWidth - 10) {
        $dropdown.addClass('align-right');
      } else {
        $dropdown.removeClass('align-right');
      }

      // If menu extends beyond bottom edge, show it above
      const viewportHeight = window.innerHeight;
      if (menuRect.bottom > viewportHeight - 10) {
        $dropdown.addClass('align-top');
      } else {
        $dropdown.removeClass('align-top');
      }
    });

    // Close on mouse leave with slight delay to allow moving to menu
    $dropdown.on('mouseleave', function(e) {
      // Small delay to allow mouse to move to menu without closing
      closeTimeout = setTimeout(function() {
        $dropdown.removeClass('open');
      }, 100);
    });

    // Keep open when hovering menu
    $menu.on('mouseenter', function() {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
    });

    // Handle dropdown item clicks
    $menu.find('.dropdown-item').on('click', function(e) {
      // Don't stop propagation - let delegated handlers work
      // Close dropdown after a small delay to allow other handlers to execute first
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
      // Delay closing to ensure click handlers fire first
      setTimeout(function() {
        $dropdown.removeClass('open');
      }, 50);
    });
  });
}

// ============================================
// Legacy Overlay Function Wrappers
// ============================================
// These functions maintain backwards compatibility by wrapping the global RowOverlay utility
// New code should use RowOverlay.show/update/hide directly

window.showAssetRowOverlay = function($row, message, type = 'loading') {
  if (window.RowOverlay && typeof window.RowOverlay.show === 'function') {
    window.RowOverlay.show($row, message, type);
  } else {
    console.warn('RowOverlay utility not loaded');
  }
};

window.updateAssetRowOverlay = function($row, message, type = 'success') {
  if (window.RowOverlay && typeof window.RowOverlay.update === 'function') {
    window.RowOverlay.update($row, message, type);
  } else {
    console.warn('RowOverlay utility not loaded');
  }
};

window.hideAssetRowOverlay = function($row) {
  if (window.RowOverlay && typeof window.RowOverlay.hide === 'function') {
    window.RowOverlay.hide($row);
  } else {
    console.warn('RowOverlay utility not loaded');
  }
};

// Expose VisualAssetsManager globally for debugging and external access
window.VisualAssetsManager = VisualAssetsManager;

// Initialize on document ready
$(document).ready(() => {
  VisualAssetsManager.init();
  initializeDropdownButtons();
});
